import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Activity, ArrowLeft, Cloud, History, RefreshCw, Shield } from 'lucide-react'
import {
  fetchSyncLogs,
  fetchSyncStatus,
  fetchThreatDownSyncLogs,
  fetchThreatDownSyncStatus,
  notifySyncStatusChanged,
  triggerSync,
  triggerThreatDownSync,
} from '@/api/sync'
import { fetchZabbixSettings, triggerZabbixSync } from '@/api/zabbix'
import { HorizontalScrollArea } from '@/components/HorizontalScrollArea'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/AuthContext'
import { cn } from '@/lib/utils'

function formatWhen(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB')
}

function formatStatus(status) {
  if (!status) return '—'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function syncErrorSummary(errors) {
  if (!Array.isArray(errors) || errors.length === 0) return null
  const fatal = errors.find((entry) => entry?.fatal)?.fatal
  if (fatal) return String(fatal).trim()
  const patchCount = errors.filter((entry) => entry?.patch_sync).length
  if (patchCount > 0) {
    return `${patchCount} agent patch sync error(s) (often Atera API quota / 429).`
  }
  return `${errors.length} error(s) recorded on this run.`
}

function SyncTile({ to, title, description, icon: Icon }) {
  return (
    <Link
      to={to}
      className={cn(
        'rounded-xl border border-border bg-card p-6 shadow-sm transition',
        'hover:border-primary/40 hover:bg-card hover:shadow-md',
      )}
    >
      <div className="mb-4">
        <Icon className="h-7 w-7 text-primary" strokeWidth={1.75} aria-hidden />
      </div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </Link>
  )
}

function SectionShell({ title, description, children }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Button asChild variant="ghost" size="sm" className="-ml-2 h-8 px-2 text-muted-foreground">
          <Link to="/admin/sync">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to Sync
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">{children}</div>
    </div>
  )
}

function SyncOverview({
  ateraSummary,
  threatdownSummary,
  zabbixSummary,
  ateraLogCount,
  threatdownLogCount,
}) {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card px-6 py-8 shadow-sm sm:px-8">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Sync</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Manual sync controls and history for Atera, ThreatDown, and Zabbix.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <SyncTile
          to="/admin/sync?section=atera"
          title="Atera sync"
          description={ateraSummary}
          icon={Cloud}
        />
        <SyncTile
          to="/admin/sync?section=threatdown"
          title="ThreatDown sync"
          description={threatdownSummary}
          icon={Shield}
        />
        <SyncTile
          to="/admin/sync?section=zabbix"
          title="Zabbix sync"
          description={zabbixSummary}
          icon={Activity}
        />
        <SyncTile
          to="/admin/sync?section=atera-history"
          title="Atera sync history"
          description={`${ateraLogCount} recent run${ateraLogCount === 1 ? '' : 's'}`}
          icon={History}
        />
        <SyncTile
          to="/admin/sync?section=threatdown-history"
          title="ThreatDown sync history"
          description={`${threatdownLogCount} recent run${threatdownLogCount === 1 ? '' : 's'}`}
          icon={History}
        />
      </div>
    </div>
  )
}

export function SyncControl() {
  const { isAdmin } = useAuth()
  const [params] = useSearchParams()
  const section = params.get('section')
  const [status, setStatus] = useState(null)
  const [threatdownStatus, setThreatdownStatus] = useState(null)
  const [logs, setLogs] = useState([])
  const [threatdownLogs, setThreatdownLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [threatdownSyncing, setThreatdownSyncing] = useState(false)
  const [zabbixSettings, setZabbixSettings] = useState(null)
  const [zabbixSyncing, setZabbixSyncing] = useState(false)
  const [message, setMessage] = useState('')
  const [threatdownMessage, setThreatdownMessage] = useState('')
  const [zabbixMessage, setZabbixMessage] = useState('')

  const refresh = useCallback(async () => {
    const [s, td, l, tdLogs, zabbix] = await Promise.all([
      fetchSyncStatus(),
      fetchThreatDownSyncStatus(),
      fetchSyncLogs(15),
      fetchThreatDownSyncLogs(15),
      fetchZabbixSettings().catch(() => null),
    ])
    setStatus(s)
    setThreatdownStatus(td)
    setLogs(l)
    setThreatdownLogs(tdLogs)
    setZabbixSettings(zabbix)
    notifySyncStatusChanged()
  }, [])

  useEffect(() => {
    refresh()
      .catch(() => setMessage('Could not load sync status.'))
      .finally(() => setLoading(false))
  }, [refresh])

  async function handleThreatDownSync() {
    setThreatdownSyncing(true)
    setThreatdownMessage('')
    try {
      const result = await triggerThreatDownSync()
      if (result.status === 'skipped') {
        setThreatdownMessage(result.reason || 'ThreatDown sync skipped.')
      } else if (result.status === 'error') {
        setThreatdownMessage(result.reason || 'ThreatDown sync failed.')
      } else {
        setThreatdownMessage(
          `ThreatDown sync finished — ${result.synced ?? 0} endpoints, ${result.detections_synced ?? 0} detections updated.`,
        )
      }
      await refresh()
    } catch (err) {
      setThreatdownMessage(err.response?.data?.detail || 'ThreatDown sync failed.')
    } finally {
      setThreatdownSyncing(false)
    }
  }

  async function handleZabbixSync() {
    setZabbixSyncing(true)
    setZabbixMessage('')
    try {
      const result = await triggerZabbixSync()
      if (result.status === 'ok') {
        const removed = result.removed ? `, ${result.removed} removed` : ''
        setZabbixMessage(`Zabbix sync finished — ${result.synced ?? 0} hosts${removed}.`)
      } else {
        setZabbixMessage(result.reason || 'Zabbix sync did not complete.')
      }
      await refresh()
    } catch (err) {
      setZabbixMessage(err.response?.data?.detail || 'Zabbix sync failed.')
    } finally {
      setZabbixSyncing(false)
    }
  }

  async function handleSync() {
    setSyncing(true)
    setMessage('Sync started — this can take a minute or two…')
    const previousId = status?.last_sync?.id ?? null
    try {
      await triggerSync()
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Could not start sync.')
      setSyncing(false)
      await refresh().catch(() => {})
      return
    }

    const startedAt = Date.now()
    const maxWaitMs = 10 * 60 * 1000
    let sawRunning = false
    try {
      while (Date.now() - startedAt < maxWaitMs) {
        await new Promise((resolve) => setTimeout(resolve, 2500))
        let current
        try {
          current = await fetchSyncStatus()
        } catch {
          continue
        }
        setStatus(current)
        if (current.running) {
          sawRunning = true
          continue
        }
        const finished = current.last_sync
        const isNewRun = finished && finished.id !== previousId && finished.completed_at
        if (sawRunning || isNewRun) {
          const records = finished?.records_synced ?? 0
          if (finished?.status === 'success') {
            setMessage(`Sync finished successfully — ${records} records updated.`)
          } else if (finished?.status === 'failed') {
            setMessage('Sync failed. Check the sync history and server logs for details.')
          } else {
            setMessage(`Sync finished: ${finished?.status ?? 'unknown'} — ${records} records.`)
          }
          await refresh().catch(() => {})
          return
        }
      }
      setMessage('Sync is taking longer than expected — check the sync history shortly.')
      await refresh().catch(() => {})
    } finally {
      setSyncing(false)
    }
  }

  const last = status?.last_sync
  const lastThreatdown = threatdownStatus?.last_sync
  const ateraErrorDetail = syncErrorSummary(last?.errors)

  const ateraSummary = last
    ? `${formatWhen(last.completed_at || last.started_at)} · ${last.status || 'unknown'}`
    : 'No sync runs yet'

  const threatdownSummary = lastThreatdown
    ? `${formatWhen(lastThreatdown.completed_at || lastThreatdown.started_at)} · ${formatStatus(lastThreatdown.status)}`
    : 'No sync runs yet'

  const zabbixConfigured = Boolean(zabbixSettings?.base_url && zabbixSettings?.api_token_configured)
  const zabbixSummary = !zabbixSettings
    ? 'Not loaded'
    : zabbixSettings.last_synced_at
      ? `${formatWhen(zabbixSettings.last_synced_at)} · ${formatStatus(zabbixSettings.last_status)}${
          zabbixSettings.last_host_count != null ? ` · ${zabbixSettings.last_host_count} hosts` : ''
        }`
      : zabbixConfigured
        ? 'No sync runs yet'
        : 'Not configured'

  if (section === 'atera') {
    return (
      <SectionShell
        title="Atera sync"
        description="Pull customers, devices, hardware, software, and patches from Atera."
      >
        {loading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <p>
                <span className="text-muted-foreground">Last run: </span>
                {formatWhen(last?.completed_at || last?.started_at)}
              </p>
              <p>
                <span className="text-muted-foreground">Status: </span>
                <span className="capitalize">{last?.status || '—'}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Records: </span>
                {last?.records_synced ?? '—'}
              </p>
              <p>
                <span className="text-muted-foreground">Triggered by: </span>
                {last?.triggered_by || '—'}
              </p>
              {ateraErrorDetail ? (
                <p className="sm:col-span-2">
                  <span className="text-muted-foreground">Detail: </span>
                  {ateraErrorDetail}
                </p>
              ) : null}
            </div>
            {isAdmin ? (
              <Button onClick={handleSync} disabled={syncing || status?.running}>
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing…' : 'Sync now'}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Only administrators can trigger a manual sync.
              </p>
            )}
            {message ? <p className="text-sm">{message}</p> : null}
          </div>
        )}
      </SectionShell>
    )
  }

  if (section === 'threatdown') {
    return (
      <SectionShell
        title="ThreatDown sync"
        description="Pull endpoints and detections from ThreatDown OneView."
      >
        {loading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <p>
                <span className="text-muted-foreground">Last run: </span>
                {formatWhen(lastThreatdown?.completed_at || lastThreatdown?.started_at)}
              </p>
              <p>
                <span className="text-muted-foreground">Status: </span>
                {formatStatus(lastThreatdown?.status)}
              </p>
              <p>
                <span className="text-muted-foreground">Endpoints: </span>
                {lastThreatdown?.endpoints_synced ?? '—'}
              </p>
              <p>
                <span className="text-muted-foreground">Triggered by: </span>
                {lastThreatdown?.triggered_by || '—'}
              </p>
              {lastThreatdown?.reason ? (
                <p className="sm:col-span-2">
                  <span className="text-muted-foreground">Detail: </span>
                  {lastThreatdown.reason}
                </p>
              ) : null}
            </div>
            {isAdmin ? (
              <Button
                onClick={handleThreatDownSync}
                disabled={threatdownSyncing || threatdownStatus?.running}
                variant="secondary"
              >
                <RefreshCw className={`h-4 w-4 ${threatdownSyncing ? 'animate-spin' : ''}`} />
                {threatdownSyncing ? 'Syncing…' : 'Sync ThreatDown now'}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Only administrators can trigger ThreatDown sync.
              </p>
            )}
            {threatdownMessage ? <p className="text-sm">{threatdownMessage}</p> : null}
          </div>
        )}
      </SectionShell>
    )
  }

  if (section === 'zabbix') {
    return (
      <SectionShell
        title="Zabbix sync"
        description="Pull the Zabbix host list used by the coverage report and Open in Zabbix."
      >
        {loading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="space-y-4">
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <p>
                <span className="text-muted-foreground">Last run: </span>
                {formatWhen(zabbixSettings?.last_synced_at)}
              </p>
              <p>
                <span className="text-muted-foreground">Status: </span>
                {formatStatus(zabbixSettings?.last_status)}
              </p>
              <p>
                <span className="text-muted-foreground">Hosts: </span>
                {zabbixSettings?.last_host_count ?? '—'}
              </p>
              <p>
                <span className="text-muted-foreground">URL: </span>
                {zabbixSettings?.base_url || '—'}
              </p>
              {zabbixSettings?.last_error ? (
                <p className="sm:col-span-2">
                  <span className="text-muted-foreground">Detail: </span>
                  {zabbixSettings.last_error}
                </p>
              ) : null}
            </div>
            {zabbixConfigured ? (
              <Button onClick={handleZabbixSync} disabled={zabbixSyncing} variant="secondary">
                <RefreshCw className={`h-4 w-4 ${zabbixSyncing ? 'animate-spin' : ''}`} />
                {zabbixSyncing ? 'Syncing…' : 'Sync Zabbix now'}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Add the Zabbix URL and API token under{' '}
                <Link to="/admin/settings?section=zabbix" className="text-primary hover:underline">
                  Settings → Zabbix
                </Link>
                .
              </p>
            )}
            {zabbixMessage ? <p className="text-sm">{zabbixMessage}</p> : null}
          </div>
        )}
      </SectionShell>
    )
  }

  if (section === 'atera-history') {
    return (
      <SectionShell
        title="Atera sync history"
        description={`${logs.length} recent run${logs.length === 1 ? '' : 's'}`}
      >
        <HorizontalScrollArea>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Completed</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Records</th>
                <th className="px-3 py-2">Trigger</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-b border-border/60">
                  <td className="px-3 py-2">{formatWhen(log.started_at)}</td>
                  <td className="px-3 py-2">{formatWhen(log.completed_at)}</td>
                  <td className="px-3 py-2 capitalize">{log.status}</td>
                  <td className="px-3 py-2">{log.records_synced ?? '—'}</td>
                  <td className="px-3 py-2">{log.triggered_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </HorizontalScrollArea>
      </SectionShell>
    )
  }

  if (section === 'threatdown-history') {
    return (
      <SectionShell
        title="ThreatDown sync history"
        description={`${threatdownLogs.length} recent run${threatdownLogs.length === 1 ? '' : 's'}`}
      >
        <HorizontalScrollArea>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Completed</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Endpoints</th>
                <th className="px-3 py-2">Trigger</th>
              </tr>
            </thead>
            <tbody>
              {threatdownLogs.map((log) => (
                <tr key={log.id} className="border-b border-border/60">
                  <td className="px-3 py-2">{formatWhen(log.started_at)}</td>
                  <td className="px-3 py-2">{formatWhen(log.completed_at)}</td>
                  <td className="px-3 py-2">{formatStatus(log.status)}</td>
                  <td className="px-3 py-2">{log.endpoints_synced ?? '—'}</td>
                  <td className="px-3 py-2">{log.triggered_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </HorizontalScrollArea>
      </SectionShell>
    )
  }

  return (
    <SyncOverview
      ateraSummary={loading ? 'Loading…' : ateraSummary}
      threatdownSummary={loading ? 'Loading…' : threatdownSummary}
      zabbixSummary={loading ? 'Loading…' : zabbixSummary}
      ateraLogCount={logs.length}
      threatdownLogCount={threatdownLogs.length}
    />
  )
}
